/**
 * The star chart: the field the graph sits inside.
 *
 * WHY THIS IS CHEAP, which is the only reason it is allowed to exist. `force-graph` already runs a
 * canvas loop with `autoPauseRedraw(false)`, because the hover highlight has to repaint after the
 * simulation settles. So these frames are already being spent, and drawing into `onRenderFramePre`
 * costs a few hundred arcs rather than a second render loop.
 *
 * WHAT IT IS NOT. Not a background image, because the graph's background is deliberately
 * transparent so it inherits the page and works on both skins. Not parallax either: the stars are
 * drawn in GRAPH space, so they pan and zoom with the nodes. That is the honest choice, because the
 * graph is not a window onto a sky, it is a chart that lives in one, and stars that slid against
 * the nodes would read as two layers rather than one place.
 *
 * TILED, NOT A FIXED SET, and this is the whole rewrite of 2026-08-27. The first version scattered
 * 260 stars across a 10,400-unit square, which sounds like a lot and is not: the visible rectangle
 * at the graph's default zoom is a small fraction of that area, so about THREE stars were on screen
 * and he reported, correctly, that the graph had no sky at all. The same mistake was in the deck's
 * CSS field. A fixed set also gets sparser the further you zoom in, which is backwards.
 *
 * So the field is now generated per CELL: the visible rectangle is walked as a grid, each cell's
 * contents come from a hash of its integer coordinates, and nothing is stored. Density is therefore
 * constant at every zoom and pan, the field is infinite, it is identical on every mount, and the
 * cost is bounded by what is on screen rather than by how big the sky is.
 *
 * THREE CONSTRAINTS, from the plan, all enforced here rather than trusted to the caller:
 *  1. `prefers-reduced-motion` removes the twinkle entirely. The field stays, still.
 *  2. Only what is on screen is drawn, so zooming in costs no more than zooming out.
 *  3. The colour follows the theme, sampled at most once a second rather than every frame:
 *     `getComputedStyle` in a render loop is the classic way to make a canvas janky.
 */

export interface Starfield {
  draw(ctx: CanvasRenderingContext2D, globalScale: number): void;
}

/**
 * Cell size in graph units, and stars per cell. Together these are the density dial: one star per
 * 150x150 graph units puts roughly forty on a default-zoom screen, which reads as a sky without
 * competing with the nodes. Tuned by measuring the visible rectangle, not by eye.
 */
const CELL = 150;
const PER_CELL = 1;

/** How many cells we will draw before giving up, so a pathological zoom-out cannot stall a frame. */
const MAX_CELLS = 4000;

/** Integer hash: two cell coordinates and a lane index in, a stable 32-bit value out. */
function hash(cx: number, cy: number, lane: number): number {
  let h = 2166136261 ^ (cx * 374761393) ^ (cy * 668265263) ^ (lane * 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}

/** The hash as a 0..1 float. */
function unit(cx: number, cy: number, lane: number): number {
  return hash(cx, cy, lane) / 4294967296;
}

export function makeStarfield(el: HTMLElement): Starfield {
  const still = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  /*
   * Sampled from the theme, but NOT from --text-faint any more. That token is a body-text grey and
   * on the dark skin it produced a field the same colour and weight as the links, so the sky read
   * as more graph. Stars are near-white with a warm and a cold minority, which is what makes a
   * field look like distance rather than texture.
   */
  let base = '255,255,255';
  let sampledAt = 0;
  const sampleColour = (now: number): void => {
    if (now - sampledAt < 1000) return;
    sampledAt = now;
    /* Read from the ROOT, not from `el`. `color-scheme` is inherited, so the descendant would
       usually agree, but the root is where the theme is actually stamped and it cannot be
       overridden by something a container happens to set. */
    const root = el.ownerDocument?.documentElement ?? el;
    const dark = getComputedStyle(root).getPropertyValue('color-scheme').includes('dark');
    base = dark ? '255,255,255' : '90,110,150';
  };

  return {
    draw(ctx, globalScale) {
      const now = performance.now();
      sampleColour(now);

      /*
       * The visible rectangle in graph coordinates, from the transform the caller already applied.
       * Everything below is derived from it, which is what makes the field track zoom and pan.
       */
      const t = ctx.getTransform();
      const dpr = globalThis.devicePixelRatio || 1;
      const w = ctx.canvas.width / dpr;
      const h = ctx.canvas.height / dpr;
      const left = -t.e / t.a;
      const top = -t.f / t.d;
      const right = left + w / t.a;
      const bottom = top + h / t.d;

      const c0 = Math.floor(left / CELL);
      const c1 = Math.floor(right / CELL);
      const r0 = Math.floor(top / CELL);
      const r1 = Math.floor(bottom / CELL);
      if ((c1 - c0 + 1) * (r1 - r0 + 1) > MAX_CELLS) return;

      ctx.save();
      for (let cx = c0; cx <= c1; cx += 1) {
        for (let cy = r0; cy <= r1; cy += 1) {
          for (let k = 0; k < PER_CELL; k += 1) {
            const x = (cx + unit(cx, cy, k * 4 + 1)) * CELL;
            const y = (cy + unit(cx, cy, k * 4 + 2)) * CELL;
            const m = unit(cx, cy, k * 4 + 3);
            // Mostly small and mid-bright, a few large and bright. The cube is what keeps the
            // bright ones rare enough to read as individual stars.
            const r = 0.5 + m ** 3 * 2.1;
            const a = 0.3 + m ** 2 * 0.65;
            const phase = unit(cx, cy, k * 4 + 4) * Math.PI * 2;
            // Twinkle is a slow breath, not a blink. Off entirely when motion is reduced.
            const pulse = still ? 1 : 0.78 + 0.22 * Math.sin(now / 1400 + phase);
            // A cold and a warm minority, so the field has depth instead of one flat grey.
            const tint = m > 0.86 ? '190,215,255' : m < 0.12 ? '255,236,196' : base;
            ctx.fillStyle = `rgba(${tint},${(a * pulse).toFixed(3)})`;
            ctx.beginPath();
            ctx.arc(x, y, r / globalScale, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      ctx.restore();
    },
  };
}
